import { useState } from "react";
import dayjs from "dayjs";
import { Modal } from "../UI/Modal/Modal.tsx";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { ErrorFeedback } from "../UI/Feedback/ErrorFeedback.tsx";
import { InfoFeedback } from "../UI/Feedback/InfoFeedback.tsx";
import { useCreateReferralServiceAxiosInstance } from "./useCreateReferralServiceAxiosInstance.ts";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export function ManuallyEnqueueReferralWonModal({ isOpen, onClose }: Props) {
  const [invitedByEmail, setInvitedByEmail] = useState("");
  const [inviteeEmail, setInviteeEmail] = useState("");
  const [purchasedAt, setPurchasedAt] = useState(dayjs().format("YYYY-MM-DD"));
  const [toBeSentAt, setToBeSentAt] = useState(dayjs().format("YYYY-MM-DD"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { createReferralServiceAxiosInstance } =
    useCreateReferralServiceAxiosInstance();

  const handleClose = () => {
    setInvitedByEmail("");
    setInviteeEmail("");
    setPurchasedAt(dayjs().format("YYYY-MM-DD"));
    setToBeSentAt(dayjs().format("YYYY-MM-DD"));
    setError(null);
    setSuccess(false);
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);
    setLoading(true);
    try {
      const instance = createReferralServiceAxiosInstance();
      await instance.post("/referral/manually-enqueue-referral-won", {
        invitedByEmail,
        inviteeEmail,
        productName: "Norma-platform",
        purchasedAt: dayjs(purchasedAt).toISOString(),
        toBeSentAt: dayjs(toBeSentAt).toISOString(),
      });
      setSuccess(true);
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : "Errore durante l'operazione";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    invitedByEmail.trim() !== "" && inviteeEmail.trim() !== "";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={"Add Manual Referral Won"}
    >
      {success ? (
        <div className={"space-y-4"}>
          <InfoFeedback
            title={"Operazione completata"}
            message={
              "Il buono è stato accodato correttamente e verrà processato dal job al prossimo run."
            }
          />
          <div className={"flex justify-center mt-4"}>
            <PrimaryButton label={"Chiudi"} onClick={handleClose} />
          </div>
        </div>
      ) : (
        <div className={"space-y-4"}>
          <div>
            <label className={"block text-sm font-medium mb-1"}>
              Email invitante
            </label>
            <input
              type="email"
              value={invitedByEmail}
              onChange={(e) => setInvitedByEmail(e.target.value)}
              placeholder="chi ha invitato"
              className={"w-full px-3 py-2 border border-gray-300 rounded-md"}
            />
          </div>
          <div>
            <label className={"block text-sm font-medium mb-1"}>
              Email invitato
            </label>
            <input
              type="email"
              value={inviteeEmail}
              onChange={(e) => setInviteeEmail(e.target.value)}
              placeholder="chi è stato invitato"
              className={"w-full px-3 py-2 border border-gray-300 rounded-md"}
            />
          </div>
          <div>
            <label className={"block text-sm font-medium mb-1"}>
              Data di acquisto
            </label>
            <input
              type="date"
              value={purchasedAt}
              onChange={(e) => setPurchasedAt(e.target.value)}
              className={"w-full px-3 py-2 border border-gray-300 rounded-md"}
            />
          </div>
          <div>
            <label className={"block text-sm font-medium mb-1"}>
              Data di invio
            </label>
            <input
              type="date"
              value={toBeSentAt}
              onChange={(e) => setToBeSentAt(e.target.value)}
              className={"w-full px-3 py-2 border border-gray-300 rounded-md"}
            />
          </div>
          {error && <ErrorFeedback title={"Errore"} message={error} />}
          <div className={"flex justify-center mt-6"}>
            <PrimaryButton
              label={"Accoda buono"}
              onClick={handleSubmit}
              loading={loading}
              disabled={!isFormValid || loading}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
